import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  ChatMessage,
  SessionResponse,
  MessageResponse,
} from './types';

const SESSION_KEY = 'rw-cs-session';

function getApiBaseUrl(): string {
  return import.meta.env.VITE_CS_API_URL || 'http://localhost:3001/api';
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  // Restore session from localStorage
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const { sessionId: sid, messages: msgs } = JSON.parse(saved);
        if (sid && msgs?.length) {
          setSessionId(sid);
          setMessages(msgs);
          return;
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
  }, []);

  // Persist session to localStorage
  const persist = useCallback(
    (sid: string, msgs: ChatMessage[]) => {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ sessionId: sid, messages: msgs }));
    },
    [],
  );

  // Create a new session
  const initSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/chat/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('세션 생성에 실패했습니다.');
      const data: SessionResponse = await res.json();
      const greeting: ChatMessage = {
        role: 'assistant',
        content: data.message.content,
        buttons: data.message.buttons,
      };
      setSessionId(data.sessionId);
      setMessages([greeting]);
      persist(data.sessionId, [greeting]);
    } catch (err: any) {
      setError(err.message || '연결 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [persist]);

  // Send a message (text or button)
  const sendMessage = useCallback(
    async (type: 'text' | 'button', content: string) => {
      if (!sessionId) return;
      const userMsg: ChatMessage = { role: 'user', content };
      const next = [...messages, userMsg];
      setMessages(next);
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${getApiBaseUrl()}/chat/sessions/${sessionId}/messages`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type, content }),
          },
        );

        if (res.status === 404 || res.status === 401) {
          // Session expired — reset
          localStorage.removeItem(SESSION_KEY);
          setSessionId(null);
          setMessages([]);
          await initSession();
          return;
        }

        if (!res.ok) throw new Error('메시지 전송에 실패했습니다.');
        const data: MessageResponse = await res.json();
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: data.message.content,
          buttons: data.message.buttons,
        };
        const updated = [...next, assistantMsg];
        setMessages(updated);
        persist(sessionId, updated);
      } catch (err: any) {
        setError(err.message || '전송 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, messages, initSession, persist],
  );

  // Reset conversation
  const resetChat = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    sessionId,
    error,
    initSession,
    sendMessage,
    resetChat,
  };
}
